import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  pairwise,
} from 'rxjs/operators';
import { isEmpty, isEqual } from 'lodash';
import {
  ByteUnit,
  DefaultConst,
  FormType,
  PlanRecurrenceType,
  Speed,
  TrafficLabelId,
  UtilsService,
  BillToAccounts,
} from 'src/app/core';
import { AppState } from 'src/app/core/reducers';
import { MultiStepFormStep } from 'src/app/shared/components/multi-step-form/multi-step-form-step.class';
import { MultiStepFormComponent } from 'src/app/shared/components/multi-step-form/multi-step-form.component';
import { TransactionReason } from 'src/app/dashboard/mobile/end-users/enums';
import { brandUuid } from 'src/app/core/store/brands/brands.selectors';

@UntilDestroy()
@Component({
  selector: 'app-apply-plan-form',
  templateUrl: './apply-plan-form.component.html',
  styleUrls: ['./apply-plan-form.component.less'],
})
export class ApplyPlanFormComponent
  extends MultiStepFormStep
  implements OnInit
{
  @Input() billToAccountsList: BillToAccounts[];
  @Input() multiStepForm: MultiStepFormComponent;
  @Input() APPLY_PLAN_FORM_PREFIX: string;
  stepForm: FormGroup;
  isLoading: boolean;
  DefaultConst = DefaultConst;
  TransactionReason = TransactionReason;
  transactionReasons: string[] = Object.keys(TransactionReason);
  byteUnits: string[] = Object.keys(ByteUnit)
    .filter((unit) => unit === ByteUnit.GB || unit === ByteUnit.MB)
    .reverse();
  isCustomizablePlan: boolean = false;
  durationMin: number = 1;
  durationMax: number = 30;
  capacityMin: number;
  capacityMax: number;
  voiceSmsMin: number = 1;
  voiceSmsMax: number = 44640;
  finalPriceMin: number = 1;
  finalPriceMax: number = 999999;
  isFirstFormDataSubscription: boolean = true;
  brandBillToAccount: string;

  @Output() onStepFormChange: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    protected fb: FormBuilder,
    protected store: Store<AppState>,
    private utilsService: UtilsService
  ) {
    super(fb, store);

    this.store
      .pipe(select(brandUuid), untilDestroyed(this))
      .subscribe((brandId: string) => {
        this.brandId = brandId;
      });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.capacityMin = this.utilsService.convertMibToGib(1); // Gib value (equivalent of 1MB)
    this.capacityMax = 100; // Gib value
    this._buildForm();
    this._showExtraFields();
    this._formValueChanges();
    this.multiStepForm.multiStepFormFormData
      .pipe(
        untilDestroyed(this),
        filter((formData) => !isEmpty(formData)),
        pairwise()
      )
      .subscribe((formData) => {
        const prev = formData[0];
        const current = formData[1];
        let isDifferentPlan = !isEqual(prev.plan?.planId, current.plan?.planId);

        // Update customizable fields' values when selecting another plan
        if (
          isDifferentPlan ||
          (this.multiStepForm.isEditMode && this.isFirstFormDataSubscription)
        ) {
          this._showExtraFields(current.plan);
          this.isFirstFormDataSubscription = false;
        }
      });
  }

  private _buildForm(): void {
    let tempTransactionReason = null;

    if (this.multiStepForm.entity.memo) {
      tempTransactionReason = this.transactionReasons
        .filter(
          (transactionReason) => transactionReason !== TransactionReason.OTHER
        )
        .includes(this.multiStepForm.entity.memo)
        ? this.multiStepForm.entity.memo
        : TransactionReason.OTHER;
    }
    this.stepForm = this.fb.group({
      billToAccountId: this.fb.control({
        value: this.multiStepForm.entity.billToAccountId || 'DEFAULT',
        disabled: this.multiStepForm.isEditMode,
      }),
      memo: this.fb.control({
        value: this.multiStepForm.entity.memo,
        disabled: this.multiStepForm.isEditMode,
      }),
      temp_transactionReason: this.fb.control({
        value: tempTransactionReason,
        disabled: this.multiStepForm.isEditMode,
      }),
    });
  }

  private _showExtraFields(plan?: any): void {
    switch (this.multiStepForm.formType) {
      case FormType.GRANT_PLAN:
      case FormType.PLAN_SUBSCRIPTION:
      case FormType.PRE_PROVISION_SIM:
        this.isCustomizablePlan = plan?.isCustomizable || plan?.isPersonalPlan;
        break;
      default:
        break;
    }

    if (this.isCustomizablePlan) {
      this._setExtraFieldsControls();
      if (plan) {
        this._setExtraFieldsValues(plan);
      }
      this._tempCapacityUnitValueChange();
    } else if (this.stepForm.get('temp_plan')) {
      this.stepForm.get('temp_plan.duration').clearValidators();
      this.stepForm.get('temp_plan.capacity').clearValidators();
      this.stepForm.get('temp_plan.temp_capacityUnit').clearValidators();
      this.stepForm.get('temp_plan.voice').clearValidators();
      this.stepForm.get('temp_plan.sms').clearValidators();
      this.stepForm.get('temp_plan.finalPrice').clearValidators();
    }

    if (this.stepForm.get('temp_plan.duration')) {
      this.stepForm.get('temp_plan.duration').updateValueAndValidity();
    }
    if (this.stepForm.get('temp_plan.capacity')) {
      this.stepForm.get('temp_plan.capacity').updateValueAndValidity();
    }
    if (this.stepForm.get('temp_plan.temp_capacityUnit')) {
      this.stepForm.get('temp_plan.temp_capacityUnit').updateValueAndValidity();
    }
    if (this.stepForm.get('temp_plan.voice')) {
      this.stepForm.get('temp_plan.voice').updateValueAndValidity();
    }
    if (this.stepForm.get('temp_plan.sms')) {
      this.stepForm.get('temp_plan.sms').updateValueAndValidity();
    }
    if (this.stepForm.get('temp_plan.finalPrice')) {
      this.stepForm.get('temp_plan.finalPrice').updateValueAndValidity();
    }
  }

  private _setExtraFieldsControls(): void {
    const capacity = this.utilsService.convertKibBasedOnByteUnits(
      this.multiStepForm.entity.plan?.capacity,
      this.byteUnits
    );

    const action = !this.stepForm.get('temp_plan')
      ? 'addControl'
      : 'setControl';

    this.stepForm[action](
      'temp_plan',
      this.fb.group({
        duration: this.fb.control(
          this.multiStepForm.entity.plan?.duration
            ? this.utilsService.convertSecToDay(
                this.multiStepForm.entity.plan?.duration
              )
            : 0,
          [
            Validators.required,
            Validators.min(this.durationMin),
            Validators.max(this.durationMax),
          ]
        ),
        capacity: this.fb.control(capacity.value, [
          Validators.required,
          Validators.min(this.capacityMin),
          Validators.max(this.capacityMax),
        ]),
        temp_capacityUnit: this.fb.control(
          capacity.unit || ByteUnit.GB,
          Validators.required
        ),
        voice: this.fb.group({
          initial: this.fb.control(
            this.multiStepForm.entity.plan?.voice?.initial
              ? this.utilsService.convertSecToMin(
                  this.multiStepForm.entity.plan?.voice?.initial
                )
              : 0,
            [Validators.min(this.voiceSmsMin), Validators.max(this.voiceSmsMax)]
          ),
          unlimited: this.fb.control(
            this.multiStepForm.entity.plan?.voice?.unlimited
          ),
        }),
        sms: this.fb.group({
          initial: this.fb.control(
            this.multiStepForm.entity.plan?.sms?.initial
              ? this.multiStepForm.entity.plan?.sms?.initial
              : 0,
            [Validators.min(this.voiceSmsMin), Validators.max(this.voiceSmsMax)]
          ),
          unlimited: this.fb.control(
            this.multiStepForm.entity.plan?.sms?.unlimited
          ),
        }),
        finalPrice: this.fb.control(
          this.multiStepForm.entity.plan?.finalPrices &&
            this.multiStepForm.entity.plan?.finalPrices.length === 1
            ? this.multiStepForm.entity.plan?.finalPrices[0].amount
            : 0,
          [
            Validators.required,
            Validators.min(this.finalPriceMin),
            Validators.max(this.finalPriceMax),
          ]
        ),
      })
    );
  }

  /**
   * @description Every time a plan is selected, the "apply plan" fields must be re-populated with the selected plan values
   * @private
   * @param {*} formData
   * @memberof ApplyPlanFormComponent
   */
  private _setExtraFieldsValues(formDataPlan: any): void {
    let plan = formDataPlan;

    if (
      +plan.duration > 0 ||
      plan.durationType === PlanRecurrenceType.MONTHLY
    ) {
      this.stepForm
        .get('temp_plan.duration')
        .setValue(
          plan.durationType === PlanRecurrenceType.MONTHLY
            ? 30
            : this.utilsService.convertSecToDay(+plan.duration)
        );
    }

    let capacity = 0;
    // Capacity doesn't exist in ConsumerPlanV2
    if (+plan.capacity > 0) capacity = +plan.capacity;
    // Capacity workaround for Plan Subscription
    if (!plan.capacity && plan.trafficLabels && plan.trafficLabels.length > 0) {
      const general = plan.trafficLabels.find(
        (trafficLabel) =>
          trafficLabel.trafficLabelId === TrafficLabelId.GENERAL &&
          trafficLabel.speed === Speed.SPEED_MAX
      );
      if (general) capacity = +general.capacity;
    }
    if (capacity > 0) {
      const convertedCapacity = this.utilsService.convertKibBasedOnByteUnits(
        capacity,
        this.byteUnits
      );
      this.stepForm.get('temp_plan.capacity').setValue(convertedCapacity.value);
      this.stepForm
        .get('temp_plan.temp_capacityUnit')
        .setValue(convertedCapacity.unit);
    }

    // Voice doesn't exist in ConsumerPlan
    this.stepForm
      .get('temp_plan.voice.initial')
      .setValue(
        +plan.voice?.initial > 0
          ? this.utilsService.convertSecToMin(+plan.voice?.initial)
          : null
      );
    this.stepForm
      .get('temp_plan.voice.unlimited')
      .setValue(plan.voice?.unlimited);

    // SMS doesn't exist in ConsumerPlan
    this.stepForm
      .get('temp_plan.sms.initial')
      .setValue(+plan.sms?.initial > 0 ? +plan.sms?.initial : null);
    this.stepForm.get('temp_plan.sms.unlimited').setValue(plan.sms?.unlimited);

    if (
      plan.finalPrices &&
      plan.finalPrices.length > 0 &&
      +plan.finalPrices[0]?.amount > 0
    ) {
      this.stepForm
        .get('temp_plan.finalPrice')
        .setValue(
          this.utilsService.convertToDollars(+plan.finalPrices[0]?.amount)
        );
    }
  }

  /**
   * Convert capacity value based on the selected unit value
   */
  private _tempCapacityUnitValueChange(): void {
    this.stepForm
      .get('temp_plan.temp_capacityUnit')
      .valueChanges.pipe(
        distinctUntilChanged(),
        pairwise(),
        untilDestroyed(this)
      )
      .subscribe(
        ([prevTempCapacityUnit, temp_capacityUnit]: [ByteUnit, ByteUnit]) => {
          let capacity = this.stepForm.get('temp_plan.capacity').value;

          if (temp_capacityUnit !== prevTempCapacityUnit) {
            if (temp_capacityUnit === ByteUnit.MB) {
              capacity = this.utilsService.convertGibToMib(capacity);
              this._updateCapacityValidators(
                1,
                this.utilsService.convertGibToMib(100)
              );
            } else {
              capacity = this.utilsService.convertMibToGib(capacity);
              this._updateCapacityValidators(
                this.utilsService.convertMibToGib(1),
                100
              );
            }
          }

          this.stepForm.get('temp_plan.capacity').setValue(capacity);
        }
      );
  }

  private _updateCapacityValidators(
    capacityMin: number,
    capacityMax: number
  ): void {
    this.capacityMin = capacityMin;
    this.capacityMax = capacityMax;
    this.stepForm
      .get('temp_plan.capacity')
      .setValidators([
        Validators.required,
        Validators.min(capacityMin),
        Validators.max(capacityMax),
      ]);
    this.stepForm.get('temp_plan.capacity').updateValueAndValidity();
  }

  private _formValueChanges(): void {
    this.stepForm.valueChanges
      .pipe(untilDestroyed(this), debounceTime(200))
      .subscribe((value) => {
        this.onStepFormChange.emit(value);
      });
  }
}
